import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Users, Search, ChevronDown, ChevronRight, Loader2, Save,
  Building2, CheckSquare, Square, Maximize2, Minimize2, X, Filter, Keyboard, Plus,
} from 'lucide-react';
import type { AccountScope, AccountPickerItem } from './types';
import { ACCOUNT_SERIES_KEYS, seriesBadgeClass } from '@/lib/accountSeries';

// ─── Props ──────────────────────────────────────────────────────────

interface AccountScopePickerProps {
  templateId: string;
  accountScope: AccountScope | null;
  onScopeChange: (scope: AccountScope | null) => void;
  /** Current series filter from the main ExportBuilder to help narrow CRM lookups */
  seriesFilter?: string[];
  isLocked?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────

const AccountScopePicker: React.FC<AccountScopePickerProps> = ({
  templateId,
  accountScope,
  onScopeChange,
  seriesFilter = [],
  isLocked = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [poppedOut, setPoppedOut] = useState(false);
  const [accounts, setAccounts] = useState<AccountPickerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [seriesTab, setSeriesTab] = useState<string | 'all'>('all');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualInput, setManualInput] = useState('');

  // Derive current state
  const mode = accountScope?.mode || 'all';
  const selectedIds = useMemo(() => new Set(accountScope?.selectedAccountIds || []), [accountScope]);

  // ─── Load Accounts via RPC — ALL accounts, no date filter ───────
  // Paginated to bypass PostgREST's default 1000-row response limit.
  const loadAccounts = useCallback(async () => {
    setLoading(true);
    const PAGE_SIZE = 1000;
    const MAX_PAGES = 50; // safety cap => 50k rows max

    try {
      // Use RPC function that gets ALL accounts from CRM + job data
      const allRows: any[] = [];
      let page = 0;
      let lastBatchSize = PAGE_SIZE;

      while (lastBatchSize === PAGE_SIZE && page < MAX_PAGES) {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data, error } = await supabase
          .rpc('get_distinct_accounts_by_series', {
            series_prefixes: null, // Load ALL series
          })
          .range(from, to);

        if (error) throw error;

        const batch = data || [];
        lastBatchSize = batch.length;
        allRows.push(...batch);
        page += 1;

        if (lastBatchSize < PAGE_SIZE) break;
      }

      if (allRows.length >= MAX_PAGES * PAGE_SIZE) {
        console.warn(
          `[AccountScopePicker] Account list hit safety cap of ${MAX_PAGES * PAGE_SIZE} rows — some accounts may be missing.`
        );
      }
      if (allRows.length > 10000) {
        console.warn(
          `[AccountScopePicker] Loaded ${allRows.length} accounts (>10,000).`
        );
      }

      const items: AccountPickerItem[] = allRows.map((row: any) => ({
        account_id: row.account_id,
        account_name: row.account_name || null,
        account_series: row.account_id ? row.account_id.charAt(0) : null,
        billed_status: row.billed_status || null,
        is_active: true,
        source: row.source || 'crm',
      }));
      setAccounts(items);
    } catch (err: any) {
      console.error('Failed to load accounts via RPC:', err);
      // Fallback to paginated direct CRM query
      try {
        const allRows: any[] = [];
        let page = 0;
        let lastBatchSize = PAGE_SIZE;

        while (lastBatchSize === PAGE_SIZE && page < MAX_PAGES) {
          const from = page * PAGE_SIZE;
          const to = from + PAGE_SIZE - 1;
          const { data, error } = await supabase
            .from('crm_accounts')
            .select('account_id, account_name, billed_status, is_active')
            .order('account_id')
            .range(from, to);
          if (error) throw error;

          const batch = data || [];
          lastBatchSize = batch.length;
          allRows.push(...batch);
          page += 1;

          if (lastBatchSize < PAGE_SIZE) break;
        }

        if (allRows.length > 10000) {
          console.warn(
            `[AccountScopePicker fallback] Loaded ${allRows.length} accounts (>10,000).`
          );
        }

        const items: AccountPickerItem[] = allRows.map((a: any) => ({
          account_id: a.account_id,
          account_name: a.account_name,
          account_series: a.account_id ? a.account_id.charAt(0) : null,
          billed_status: a.billed_status,
          is_active: a.is_active,
        }));
        setAccounts(items);
      } catch (fallbackErr: any) {
        console.error('Fallback also failed:', fallbackErr);
        toast({ title: 'Error', description: 'Failed to load accounts', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // ─── Add manual entries for selected IDs not in loaded accounts ──
  const allAccounts = useMemo(() => {
    const existingIds = new Set(accounts.map(a => a.account_id));
    const manualAccounts: AccountPickerItem[] = [];
    for (const id of Array.from(selectedIds)) {
      if (!existingIds.has(id)) {
        manualAccounts.push({
          account_id: id,
          account_name: '(manually entered)',
          account_series: id.charAt(0),
          billed_status: null,
          is_active: true,
          source: 'manual',
        });
      }
    }
    return [...accounts, ...manualAccounts];
  }, [accounts, selectedIds]);

  // ─── Filtered Accounts ──────────────────────────────────────────
  const filteredAccounts = useMemo(() => {
    let result = allAccounts;
    // Filter by series tab
    if (seriesTab !== 'all') {
      result = result.filter(a => a.account_series === seriesTab);
    }
    // Filter by search
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      result = result.filter(a =>
        a.account_id.toLowerCase().includes(s) ||
        (a.account_name || '').toLowerCase().includes(s)
      );
    }
    return result;
  }, [allAccounts, seriesTab, search]);

  // ─── Series Counts ──────────────────────────────────────────────
  const seriesCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of allAccounts) {
      const s = a.account_series || '?';
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [allAccounts]);

  const availableSeries = useMemo(() =>
    ACCOUNT_SERIES_KEYS.filter(s => (seriesCounts[s] || 0) > 0),
    [seriesCounts]
  );

  // ─── Selection Helpers ──────────────────────────────────────────
  const toggleAccount = useCallback((accountId: string) => {
    if (isLocked) return;
    const newSet = new Set(selectedIds);
    if (newSet.has(accountId)) newSet.delete(accountId);
    else newSet.add(accountId);
    onScopeChange({
      mode: 'selected',
      selectedAccountIds: Array.from(newSet),
    });
  }, [selectedIds, onScopeChange, isLocked]);

  const selectAll = useCallback(() => {
    if (isLocked) return;
    const allIds = new Set(selectedIds);
    for (const a of filteredAccounts) allIds.add(a.account_id);
    onScopeChange({
      mode: 'selected',
      selectedAccountIds: Array.from(allIds),
    });
  }, [filteredAccounts, selectedIds, onScopeChange, isLocked]);

  const deselectAll = useCallback(() => {
    if (isLocked) return;
    const idsToRemove = new Set(filteredAccounts.map(a => a.account_id));
    const remaining = Array.from(selectedIds).filter(id => !idsToRemove.has(id));
    onScopeChange({
      mode: remaining.length > 0 ? 'selected' : 'all',
      selectedAccountIds: remaining,
    });
  }, [filteredAccounts, selectedIds, onScopeChange, isLocked]);

  const selectSeries = useCallback((series: string) => {
    if (isLocked) return;
    const seriesAccounts = allAccounts.filter(a => a.account_series === series);
    const allIds = new Set(selectedIds);
    for (const a of seriesAccounts) allIds.add(a.account_id);
    onScopeChange({
      mode: 'selected',
      selectedAccountIds: Array.from(allIds),
    });
  }, [allAccounts, selectedIds, onScopeChange, isLocked]);

  const clearScope = useCallback(() => {
    if (isLocked) return;
    onScopeChange(null);
  }, [onScopeChange, isLocked]);

  const addManualAccount = useCallback(() => {
    if (isLocked) return;
    const trimmed = manualInput.trim();
    if (!trimmed) return;
    const ids = trimmed.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    const newSet = new Set(selectedIds);
    for (const id of ids) newSet.add(id);
    onScopeChange({
      mode: 'selected',
      selectedAccountIds: Array.from(newSet),
    });
    setManualInput('');
  }, [manualInput, selectedIds, onScopeChange, isLocked]);

  // ─── Save to Template ───────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const scopeData = mode === 'selected' && selectedIds.size > 0
        ? { mode: 'selected', selectedAccountIds: Array.from(selectedIds) }
        : null;
      const { error } = await supabase
        .from('export_templates')
        .update({ account_scope: scopeData, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', templateId);
      if (error) throw error;
      toast({
        title: 'Account Scope Saved',
        description: scopeData
          ? `${selectedIds.size} account(s) saved to template`
          : 'Account scope cleared — all accounts will be included',
      });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save account scope', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [templateId, mode, selectedIds]);

  // ─── Count selected in current view ─────────────────────────────
  const selectedInView = useMemo(() =>
    filteredAccounts.filter(a => selectedIds.has(a.account_id)).length,
    [filteredAccounts, selectedIds]
  );

  const allInViewSelected = filteredAccounts.length > 0 && selectedInView === filteredAccounts.length;

  // ─── Series color helper ────────────────────────────────────────
  const seriesColor = (s: string) => seriesBadgeClass(s);

  // ─── Render Account List ────────────────────────────────────────
  const renderAccountList = (maxHeight: string) => (
    <div className={`overflow-y-auto ${maxHeight}`}>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading accounts...
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground text-xs">
          <Building2 className="w-6 h-6 mx-auto mb-2 opacity-30" />
          <p>No accounts found{search ? ' matching search' : ''}.</p>
          <p className="text-[9px] text-muted-foreground/60 mt-1">
            Use the Manual button to enter account numbers directly.
          </p>
        </div>
      ) : (
        <div className="space-y-0">
          {filteredAccounts.map(account => {
            const isSelected = selectedIds.has(account.account_id);
            return (
              <div
                key={account.account_id}
                className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-secondary/40 transition-colors border-b border-border/20 ${
                  isSelected ? 'bg-primary/5' : ''
                } ${isLocked ? 'cursor-default opacity-70' : ''}`}
                onClick={() => toggleAccount(account.account_id)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleAccount(account.account_id)}
                  disabled={isLocked}
                  className="flex-shrink-0"
                />
                <Badge className={`${seriesColor(account.account_series || '')} text-[9px] px-1 py-0 flex-shrink-0`}>
                  {account.account_series || '?'}
                </Badge>
                <span className="text-xs font-mono text-foreground flex-shrink-0 w-16">
                  {account.account_id}
                </span>
                <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                  {account.account_name || '-'}
                </span>
                {(account as any).source === 'manual' && (
                  <Badge className="bg-gray-800/40 text-gray-400 border-gray-700/40 text-[7px] px-1 py-0 flex-shrink-0">
                    Manual
                  </Badge>
                )}
                {(account as any).source === 'jobs' && (
                  <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[7px] px-1 py-0 flex-shrink-0">
                    Jobs
                  </Badge>
                )}
                {account.billed_status === 'Billed' && (
                  <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[8px] px-1 py-0 flex-shrink-0">
                    Billed
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ─── Toolbar ────────────────────────────────────────────────────
  const renderToolbar = () => (
    <div className="space-y-2">
      {/* Mode Toggle */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Switch
            checked={mode === 'selected'}
            onCheckedChange={(v) => {
              if (isLocked) return;
              if (v) {
                onScopeChange({ mode: 'selected', selectedAccountIds: Array.from(selectedIds) });
              } else {
                onScopeChange(null);
              }
            }}
            disabled={isLocked}
            className="scale-75"
          />
          <span className="text-[10px] text-muted-foreground">
            {mode === 'selected' ? 'Selected Accounts Only' : 'All Accounts'}
          </span>
        </label>
        {mode === 'selected' && selectedIds.size > 0 && (
          <Badge className="bg-primary/20 text-primary border-primary/30 text-[9px] px-1.5 py-0">
            {selectedIds.size} selected
          </Badge>
        )}
      </div>

      {mode === 'selected' && (
        <>
          {/* Series Tabs */}
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => setSeriesTab('all')}
              className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                seriesTab === 'all'
                  ? 'bg-primary/20 text-primary border-primary/40'
                  : 'border-border/40 text-muted-foreground hover:border-border'
              }`}
            >
              All ({allAccounts.length})
            </button>
            {availableSeries.map(s => (
              <button
                key={s}
                onClick={() => setSeriesTab(s)}
                className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                  seriesTab === s
                    ? 'bg-primary/20 text-primary border-primary/40'
                    : 'border-border/40 text-muted-foreground hover:border-border'
                }`}
              >
                {s}-Series ({seriesCounts[s] || 0})
              </button>
            ))}
          </div>

          {/* Search + Manual */}
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search accounts..."
                className="dark-input text-xs h-7 pl-7"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowManualEntry(!showManualEntry)}
              className={`text-[9px] px-1.5 py-1 rounded border transition-colors flex items-center gap-0.5 flex-shrink-0 ${
                showManualEntry
                  ? 'bg-teal-900/20 text-teal-400 border-teal-700/40'
                  : 'border-border/40 text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Keyboard className="w-2.5 h-2.5" /> Manual
            </button>
          </div>

          {/* Manual Entry */}
          {showManualEntry && (
            <div className="flex gap-1.5 items-center bg-teal-900/10 border border-teal-700/20 rounded px-2 py-1.5">
              <Keyboard className="w-3 h-3 text-teal-400 flex-shrink-0" />
              <Input
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addManualAccount(); }}
                placeholder="Enter account # (e.g. 6009) or comma-separated"
                className="dark-input text-xs h-6 flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={addManualAccount}
                disabled={!manualInput.trim() || isLocked}
                className="h-6 text-[10px] px-2 text-teal-400 hover:text-teal-300"
              >
                <Plus className="w-3 h-3 mr-0.5" /> Add
              </Button>
            </div>
          )}

          {/* Bulk Actions */}
          {!isLocked && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="ghost" onClick={allInViewSelected ? deselectAll : selectAll} className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground">
                {allInViewSelected ? (
                  <><Square className="w-3 h-3 mr-1" /> Deselect {seriesTab !== 'all' ? `${seriesTab}-Series` : 'All'} ({filteredAccounts.length})</>
                ) : (
                  <><CheckSquare className="w-3 h-3 mr-1" /> Select {seriesTab !== 'all' ? `${seriesTab}-Series` : 'All'} ({filteredAccounts.length})</>
                )}
              </Button>
              {availableSeries.map(s => (
                <Button
                  key={s}
                  size="sm"
                  variant="ghost"
                  onClick={() => selectSeries(s)}
                  className="h-6 text-[10px] px-1.5 text-muted-foreground hover:text-foreground"
                >
                  +{s}
                </Button>
              ))}
              {selectedIds.size > 0 && (
                <Button size="sm" variant="ghost" onClick={clearScope} className="h-6 text-[10px] px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20">
                  <X className="w-3 h-3 mr-1" /> Clear All
                </Button>
              )}
            </div>
          )}

          {/* View count */}
          <div className="text-[10px] text-muted-foreground/60">
            Showing {filteredAccounts.length} accounts
            {selectedInView > 0 && <span className="text-primary ml-1">({selectedInView} selected in view)</span>}
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader
          className="pb-2 pt-3 px-4 cursor-pointer select-none"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('button')) return;
            setCollapsed(!collapsed);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {collapsed ? (
                <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform" />
              )}
              <Filter className="w-4 h-4 text-teal-400" />
              <CardTitle className="text-sm font-semibold">Account Scope</CardTitle>
              {accounts.length > 0 && (
                <Badge className="bg-slate-800/60 text-slate-300 border-slate-600/40 text-[9px] px-1.5 py-0">
                  {accounts.length.toLocaleString()} loaded
                </Badge>
              )}
              {mode === 'selected' && selectedIds.size > 0 && (
                <Badge className="bg-teal-900/30 text-teal-400 border-teal-700/40 text-[9px] px-1.5 py-0">
                  {selectedIds.size} accounts
                </Badge>
              )}
              {mode === 'all' && (
                <Badge className="bg-secondary text-muted-foreground text-[9px] px-1.5 py-0">All</Badge>
              )}

            </div>
            <div className="flex items-center gap-1.5">
              {!collapsed && mode === 'selected' && selectedIds.size > 0 && (
                <Button
                  size="sm" variant="ghost"
                  onClick={(e) => { e.stopPropagation(); setPoppedOut(true); }}
                  className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                >
                  <Maximize2 className="w-3 h-3 mr-1" /> Pop Out
                </Button>
              )}
              {!isLocked && mode === 'selected' && selectedIds.size > 0 && (
                <Button
                  size="sm" variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleSave(); }}
                  disabled={saving}
                  className="h-6 text-[10px] px-2 text-teal-400 hover:text-teal-300"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {!collapsed && (
          <CardContent className="px-4 pb-3 pt-0 space-y-2">
            {renderToolbar()}
            {mode === 'selected' && renderAccountList('max-h-[250px]')}

            {/* Selected accounts summary chips */}
            {mode === 'selected' && selectedIds.size > 0 && selectedIds.size <= 20 && (
              <div className="pt-1 border-t border-border/30">
                <p className="text-[9px] text-muted-foreground/60 mb-1">Selected:</p>
                <div className="flex flex-wrap gap-1">
                  {Array.from(selectedIds).sort().map(id => {
                    const acc = allAccounts.find(a => a.account_id === id);
                    return (
                      <Badge
                        key={id}
                        className="bg-teal-900/20 text-teal-400 border-teal-700/30 text-[9px] px-1.5 py-0 cursor-pointer hover:bg-teal-900/40 transition-colors"
                        onClick={() => toggleAccount(id)}
                      >
                        {id}
                        {acc?.account_name && acc.account_name !== '(manually entered)' && <span className="text-teal-400/60 ml-0.5 max-w-[80px] truncate">{acc.account_name}</span>}
                        {!isLocked && <X className="w-2.5 h-2.5 ml-0.5 opacity-60" />}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {mode === 'all' && (
              <div className="text-[10px] text-muted-foreground/60 bg-secondary/20 rounded px-2 py-1.5">
                <Building2 className="w-3 h-3 inline mr-1 opacity-60" />
                All accounts matching the current filters will be included in the export. Toggle the switch above to select specific accounts.
              </div>
            )}
          </CardContent>
        )}

        {/* Collapsed summary */}
        {collapsed && mode === 'selected' && selectedIds.size > 0 && (
          <CardContent className="px-4 pb-2 pt-0">
            <div className="flex flex-wrap gap-1 text-[9px] text-muted-foreground">
              {Array.from(selectedIds).sort().slice(0, 8).map(id => (
                <span key={id} className="px-1 py-0 rounded bg-teal-900/15 text-teal-400/80 border border-teal-700/20">
                  {id}
                </span>
              ))}
              {selectedIds.size > 8 && (
                <span className="text-muted-foreground/50">+{selectedIds.size - 8} more</span>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* ─── Pop-Out Dialog ─── */}
      <Dialog open={poppedOut} onOpenChange={setPoppedOut}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-teal-400" />
                <DialogTitle className="text-sm font-semibold">Account Scope — Select Accounts</DialogTitle>
                {selectedIds.size > 0 && (
                  <Badge className="bg-teal-900/30 text-teal-400 border-teal-700/40 text-[10px] px-1.5 py-0">
                    {selectedIds.size} selected
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isLocked && (
                  <Button
                    size="sm" variant="outline"
                    onClick={handleSave}
                    disabled={saving}
                    className="h-7 text-xs border-teal-700/40 text-teal-400 hover:bg-teal-900/20"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                    Save to Template
                  </Button>
                )}
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setPoppedOut(false)}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Minimize2 className="w-3 h-3 mr-1" /> Collapse
                </Button>
              </div>
            </div>
            <DialogDescription className="text-muted-foreground text-xs">
              Select specific accounts to include in this export template. Only jobs for selected accounts will appear in the output.
            </DialogDescription>
          </DialogHeader>

          <div className="px-5 py-3 border-b border-border/50 flex-shrink-0">
            {renderToolbar()}
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {renderAccountList('h-full')}
          </div>

          {/* Selected summary footer */}
          {selectedIds.size > 0 && (
            <div className="px-5 py-2 border-t border-border/40 flex-shrink-0">
              <div className="flex flex-wrap gap-1 max-h-[60px] overflow-y-auto">
                {Array.from(selectedIds).sort().map(id => {
                  const acc = allAccounts.find(a => a.account_id === id);
                  return (
                    <Badge
                      key={id}
                      className="bg-teal-900/20 text-teal-400 border-teal-700/30 text-[9px] px-1.5 py-0 cursor-pointer hover:bg-teal-900/40"
                      onClick={() => toggleAccount(id)}
                    >
                      {id} {acc?.account_name && acc.account_name !== '(manually entered)' && `- ${acc.account_name}`}
                      {!isLocked && <X className="w-2.5 h-2.5 ml-0.5 opacity-60" />}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountScopePicker;
